<template>
	<KRPlacardLayout :class="classes">
		<template #header>
			<div class="kr-placard__avatar">
				<UserAvatar :user="owner" />
			</div>
			<h1 class="kr-placard__title" v-html="name"></h1>
		</template>
		<template #mid>
			<div v-if="dueDate">
				<TeamingChip
					class="kr-placard__due-date"
					:text="dueDate"
				/>
			</div>
			<label class="kr-placard__updated-date">{{ translate( "updatedLabel", { date: lastUpdatedAgo, } ) }}</label>
		</template>
		<template #confidence>
			<label class="stack-label">{{ translate( "confidenceLabel" ) }}</label>
			<div class="stack-value">
				<div>
					<ConfidenceChip
						style="display: inline-flex;"
						:level="confidenceLevel"
						:disabled="true"
					/>
				</div>
			</div>
		</template>
		<template #progress>
			<label class="stack-label">{{ translate( "progressLabel" ) }}</label>
			<div class="kr-placard__progress stack-value">
				<div>{{ progress }}%</div>
				<TeamingProgressBar
					:percent-completed="progress"
					:percent-progressed="pacing"
					:show-percent="false"
				/>
			</div>
		</template>
		<template #current-value>
			<label class="stack-label">{{ translate( "currentLabel" ) }}</label>
			<div class="stack-value">{{ formattedCurrentValue }}</div>
		</template>
		<template #target-value>
			<label v-show="isTargetVisible" class="stack-label">{{ translate( "targetLabel" ) }}</label>
			<div v-show="isTargetVisible" class="stack-value">{{ formattedTargetValue }}</div>
		</template>
	</KRPlacardLayout>
</template>

<script>
import { ConfidenceLevel, KeyResultType } from "@teaming/common-enums";

import { KeyResultDisplayFormat } from "@teaming/common-gokr";

import {
	UserAvatar,
	TeamingChip,
	TeamingProgressBar,
	ConfidenceChip
} from "@teaming/common-ui";

import KRPlacardLayout from "./KRPlacardLayout";

export default {
	name: "KRPlacard",
	components: {
		KRPlacardLayout,
		UserAvatar,
		TeamingChip,
		TeamingProgressBar,
		ConfidenceChip,
	},
	props: {
		// eslint-disable-next-line vue/no-unused-properties
		disabled: {
			type: Boolean,
			default: false,
		},
		ownerName: {
			type: String,
			required: true,
		},
		ownerEmail: {
			type: String,
			required: true,
		},
		ownerAvatar: {
			type: String,
			default: "",
		},
		name: {
			type: String,
			required: true,
		},
		dueDate: {
			type: String,
			required: true,
		},
		// eslint-disable-next-line vue/no-unused-properties
		lastUpdatedDate: {
			type: String,
			required: true,
		},
		lastUpdatedAgo: {
			type: String,
			required: true,
		},
		confidenceLevel: {
			type: String,
			required: true,
			validator( value ) {
				return ConfidenceLevel.includes( value );
			},
		},
		progress: {
			type: Number,
			required: true,
		},
		pacing: {
			type: Number,
			required: true,
		},
		currentValue: {
			type: String,
			required: true,
		},
		targetValue: {
			type: String,
			required: true,
		},
		type: {
			type: String,
			required: true,
			validator( value ) {
				return KeyResultType.includes( value );
			},
		},
	},
	computed: {
		classes() {
			return {
				"kr-placard": true,
				clickable: !this.disabled,
			};
		},
		owner() {
			return {
				displayName: this.ownerName,
				email: this.ownerEmail,
				picture: this.ownerAvatar,
			};
		},
		isTargetVisible() {
			return this.type !== KeyResultType.BINARY;
		},
		formattedCurrentValue() {
			return KeyResultDisplayFormat[ this.type ]( this.currentValue );
		},
		formattedTargetValue() {
			return KeyResultDisplayFormat[ this.type ]( this.targetValue );
		},
	},
	methods: {
		translate( key, args = {} ) {
			return this.$t( `common-gokr.components.KRPlacard.${ key }`, args );
		},
	},
};
</script>

<style lang="scss" scoped>
$krPlacardTitleColor: $color1-700;
$krPlacardLabelFontColor: $color1-500;
$krPlacardValueFontColor: $color1-700;
$krPlacardDueDateBackgroundColor: $color1-100;
$krPlacardProgressColor: $color2-500;

.kr-placard.clickable {
	cursor: pointer;

	* {
		cursor: pointer;
	}
}

.kr-placard__title {
	@include font-and-line-for(1);
	font-weight: 600;
	color: $krPlacardTitleColor;

	@include anchor() {
		color: $anchorColor !important;
		font-weight: 600;
		text-decoration: none;
	}
}

.stack-label {
	@include font-and-line-for(-1);
	color: $krPlacardLabelFontColor;
	font-weight: 600;
}

.stack-value {
	@include font-and-line-for(0);
	color: $krPlacardValueFontColor;
	font-weight: 600;
}

.kr-placard__due-date {
	font-weight: 600;
	color: $krPlacardLabelFontColor;
	background: $krPlacardDueDateBackgroundColor;
}

.kr-placard__updated-date {
	@include font-and-line-for(-1);
	color: $krPlacardLabelFontColor;
	font-weight: 600;
}

.kr-placard__progress {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	gap: 0.5rem;

	:nth-child(2) {
		flex: 1;
	}

	color: $krPlacardProgressColor;
}
</style>
